import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, ChevronUp, ChevronDown, User, Mail, Globe, Clock, Smartphone, Check, X, Sun, Moon, Shield, Lock } from 'lucide-react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import PaypalLogo from "./assets/paypal_logo.png";
import Cardlogo from "./assets/card_logo.png";
import { useTranslation } from './useTranslation';


const BASE_URL = 'https://olahbackend.gaia-ml.com';
const STRIPE_PUBLISHABLE_KEY = "pk_live_51RoXfVGYHuUUMTi2hNtoxJpqlrNLFufqy85Z10IepfSz7VVmS8zzEpCgEyUKQDI1yXUl9nq04NEa7l8fZALi55zV00X3mi71Q7";

const Checkout = ({ user, setShowAuthModal }) => {
  const { t, language } = useTranslation();

  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOrderSummary, setShowOrderSummary] = useState(true);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [hasReferralDiscount, setHasReferralDiscount] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [referralCodeStatus, setReferralCodeStatus] = useState('');
  const [isValidatingReferralCode, setIsValidatingReferralCode] = useState(false);

  const cardElementRef = useRef(null);
  const cardContainerRef = useRef(null);
  const isCardMountedRef = useRef(false);

  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [differentEmail, setDifferentEmail] = useState(false);
  const [differentEmailAddress, setDifferentEmailAddress] = useState('');
  const [createAccount, setCreateAccount] = useState(!user);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Stripe states
  const [stripeLoading, setStripeLoading] = useState(true);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);

  // Fees
  const calculatePaymentFee = (amount) => {
    if (paymentMethod === 'stripe') {
      const baseRate = 0.029;
      const fixedFee = 0.30;
      const internationalSurcharge = 0.015;
      return (amount * (baseRate + internationalSurcharge)) + fixedFee;
    } else {
      const rate = 0.0499;
      const fixedFee = 0.30;
      return (amount * rate) + fixedFee;
    }
  };

  const totalWithFees = () => {
    const basePrice = parseFloat(planData.price);
    const finalPrice = hasReferralDiscount ? basePrice - discountAmount : basePrice;
    const processingFee = calculatePaymentFee(finalPrice);
    return finalPrice + processingFee;
  };

  const getFinalPrice = () => {
    const basePrice = parseFloat(planData.price);
    return hasReferralDiscount ? basePrice - discountAmount : basePrice;
  };

  // Referral code
  const validateReferralCode = async (code) => {
    if (code.trim().length === 0) {
      setHasReferralDiscount(false);
      setDiscountPercent(0);
      setDiscountAmount(0);
      setReferralCodeStatus('');
      setIsValidatingReferralCode(false);
      return;
    }

    setIsValidatingReferralCode(true);
    setReferralCodeStatus(t('checkout.referral.validating'));

    try {
      const db = getFirestore();
      const codeDoc = await getDoc(doc(db, 'referralCodes', code.trim().toUpperCase()));

      if (!codeDoc.exists()) {
        setHasReferralDiscount(false);
        setDiscountPercent(0);
        setDiscountAmount(0);
        setReferralCodeStatus(t('checkout.referral.invalid'));
        setIsValidatingReferralCode(false);
        return;
      }

      const data = codeDoc.data();
      const isActive = data.isActive || false;
      const usageLimit = data.usageLimit || 0;
      const usedCount = data.usedCount || 0;
      const createdBy = data.createdBy || '';
      const discountValue = data.discountValue || 0;

      if (user && createdBy === user.uid) {
        setHasReferralDiscount(false);
        setDiscountPercent(0);
        setDiscountAmount(0);
        setReferralCodeStatus(t('checkout.referral.ownCode'));
        setIsValidatingReferralCode(false);
        return;
      }

      if (!isActive) {
        setHasReferralDiscount(false);
        setDiscountPercent(0);
        setDiscountAmount(0);
        setReferralCodeStatus(t('checkout.referral.inactive'));
        setIsValidatingReferralCode(false);
        return;
      }

      if (usageLimit !== -1 && usedCount >= usageLimit) {
        setHasReferralDiscount(false);
        setDiscountPercent(0);
        setDiscountAmount(0);
        setReferralCodeStatus(t('checkout.referral.used'));
        setIsValidatingReferralCode(false);
        return;
      }

      const discount = (parseFloat(planData.price) * discountValue) / 100;
      setHasReferralDiscount(true);
      setDiscountPercent(discountValue);
      setDiscountAmount(discount);
      setReferralCodeStatus(t('checkout.referral.valid', { percent: discountValue }));

    } catch (error) {
      setHasReferralDiscount(false);
      setDiscountPercent(0);
      setDiscountAmount(0);
      setReferralCodeStatus(t('checkout.referral.error'));
      console.error('Error validating referral code:', error);
    } finally {
      setIsValidatingReferralCode(false);
    }
  };

  // Load Stripe
  useEffect(() => {
    const loadStripe = async () => {
      try {
        if (window.Stripe) {
          const stripeInstance = window.Stripe(STRIPE_PUBLISHABLE_KEY);
          setStripe(stripeInstance);
          const elementsInstance = stripeInstance.elements();
          setElements(elementsInstance);
        } else {
          const script = document.createElement('script');
          script.src = 'https://js.stripe.com/v3/';
          script.onload = () => {
            const stripeInstance = window.Stripe(STRIPE_PUBLISHABLE_KEY);
            setStripe(stripeInstance);
            const elementsInstance = stripeInstance.elements();
            setElements(elementsInstance);
          };
          script.onerror = () => {
            console.error('Failed to load Stripe');
            setStripeLoading(false);
          };
          document.head.appendChild(script);
        }
      } catch (error) {
        console.error('Error loading Stripe:', error);
        setStripeLoading(false);
      }
    };
    loadStripe();
  }, []);

  // Load PayPal
  useEffect(() => {
    const loadPayPal = async () => {
      if (window.paypal) return;
      const script = document.createElement('script');
      script.src = 'https://www.paypal.com/sdk/js?client-id=ASJ_2TTYUabuJXUAd1wdHDf2dduZIyNDNiPsn4QGx52scuT7UPuF-HmiY-QlhHvWWQ_xqcRFFhX-xhMY&currency=USD';
      script.onload = () => console.log('PayPal SDK loaded');
      script.onerror = () => console.error('Failed to load PayPal SDK');
      document.head.appendChild(script);
    };
    loadPayPal();
  }, []);

  // Mount Stripe Card element
  useEffect(() => {
    if (!elements) return;

    const createAndMountCard = () => {
      try {
        if (cardElementRef.current) {
          cardElementRef.current.unmount();
          isCardMountedRef.current = false;
        }

        const newCardElement = elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#1f2937',
              backgroundColor: '#ffffff',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: '400',
              lineHeight: '24px',
              '::placeholder': { color: '#9ca3af', fontWeight: '400' },
            },
            invalid: { color: '#dc2626' },
            complete: { color: '#059669' },
          },
        });

        newCardElement.on('change', (event) => {
          setCardError(event.error ? event.error.message : null);
          setCardComplete(event.complete);
        });

        newCardElement.on('ready', () => setStripeLoading(false));
        if (cardContainerRef.current) {
          newCardElement.mount(cardContainerRef.current);
          cardElementRef.current = newCardElement;
          setCardElement(newCardElement);
          isCardMountedRef.current = true;
          setStripeLoading(false);
        }
      } catch (error) {
        console.error('Error creating card element:', error);
        setStripeLoading(false);
      }
    };

    createAndMountCard();
    return () => {
      if (cardElementRef.current) {
        try { cardElementRef.current.unmount(); } catch {}
        isCardMountedRef.current = false;
      }
    };
  }, [elements]);

  // Read plan from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const planParam = urlParams.get('plan');
    if (planParam) {
      try {
        const decoded = JSON.parse(decodeURIComponent(planParam));
        setPlanData(decoded);
      } catch (error) {
        console.error('Error parsing plan data:', error);
        window.location.href = '/dashboard';
      }
    } else {
      window.location.href = '/dashboard';
    }
    setLoading(false);
  }, []);

  // Prefill name/email from user
  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      if (user.displayName) {
        const nameParts = user.displayName.split(' ');
        setFirstName(nameParts[0] || '');
        setLastName(nameParts.slice(1).join(' ') || '');
      }
    }
  }, [user]);

  // Render PayPal buttons
  useEffect(() => {
    if (paymentMethod === 'paypal' && window.paypal && !processing && planData) {
      const container = document.getElementById('paypal-button-container');
      if (container) {
        container.innerHTML = '';
        window.paypal.Buttons({
          style: { layout: 'vertical', color: 'blue', shape: 'rect', label: 'paypal' },
          createOrder: (data, actions) => actions.order.create({
            purchase_units: [{
              amount: { value: totalWithFees().toFixed(2) },
              description: t('checkout.paypal.description', {
                country: planData.country, data: planData.dataAmount, days: planData.duration
              })
            }]
          }),
          onApprove: async (data, actions) => {
            setProcessing(true);
            try {
              const order = await actions.order.capture();
              await handlePayPalPayment(order);
            } catch (error) {
              console.error('PayPal payment error:', error);
              alert(t('checkout.alerts.paypalFailed', { msg: error.message }));
              setProcessing(false);
            }
          },
          onError: (err) => {
            console.error('PayPal error:', err);
            alert(t('checkout.alerts.paypalGeneric'));
            setProcessing(false);
          },
          onCancel: () => {
            console.log('PayPal payment cancelled');
            setProcessing(false);
          }
        }).render('#paypal-button-container');
      }
    }
  }, [paymentMethod, planData, processing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePayPalPayment = async (paypalOrder) => {
    try {
      if (!firstName.trim() || !lastName.trim()) {
        alert(t('checkout.alerts.enterName'));
        return;
      }

      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const billingEmail = differentEmail ? differentEmailAddress : email;

      const purchaseData = {
        bundle_name: planData.planData.bundle_name || planData.planData.name,
        country_code: planData.countryCode,
        price: totalWithFees(),
        billing_details: { fullName, email: billingEmail },
        payment_method: 'paypal',
        is_guest: !user,
        marketing_opt_in: true,
        send_to_both_emails: user && differentEmail,
        original_user_email: user ? user.email : null,
        alternative_email: differentEmail ? differentEmailAddress : null,
        paypal_order_id: paypalOrder.id,
        paypal_payer_id: paypalOrder.payer.payer_id,
        referral_code: hasReferralDiscount ? referralCode.trim().toUpperCase() : null,
        original_price: parseFloat(planData.price),
        discount_amount: hasReferralDiscount ? discountAmount : null,
      };

      let headers = { 'Content-Type': 'application/json', 'User-Agent': 'Airvoy-Web/1.0' };
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }

      const completeResponse = await fetch(`${BASE_URL}/api/esims/complete-paypal-purchase/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(purchaseData)
      });

      if (completeResponse.ok) {
        const result = await completeResponse.json();
        setPaymentSuccess(true);

        const targetEmail = user && differentEmail
          ? t('checkout.success.sentBoth', { a: user.email, b: differentEmailAddress })
          : t('checkout.success.sentOne', { email: billingEmail });

        setSuccessMessage(t('checkout.success.message', { iccid: result.iccid, targetEmail }));
        setShowSuccessNotification(true);

        setTimeout(() => {
          setShowSuccessNotification(false);
          window.location.href = '/dashboard';
        }, 3000);
      } else {
        const errorData = await completeResponse.json();
        throw new Error(errorData.error || 'Failed to complete purchase');
      }
    } catch (error) {
      console.error('PayPal purchase error:', error);
      alert(t('checkout.alerts.purchaseFailed', { msg: error.message }));
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      alert(t('checkout.alerts.enterName'));
      return;
    }

    if (paymentMethod === 'stripe') {
      if (!cardElementRef.current || !isCardMountedRef.current) {
        alert(t('checkout.alerts.formNotReady'));
        return;
      }
      if (!cardComplete) {
        alert(t('checkout.alerts.cardIncomplete'));
        return;
      }
    }

    setProcessing(true);

    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const billingEmail = differentEmail ? differentEmailAddress : email;

      const purchaseData = {
        bundle_name: planData.planData.bundle_name || planData.planData.name,
        country_code: planData.countryCode,
        price: totalWithFees(),
        billing_details: { fullName, email: billingEmail },
        payment_method: paymentMethod,
        is_guest: !user,
        marketing_opt_in: true,
        send_to_both_emails: user && differentEmail,
        original_user_email: user ? user.email : null,
        referral_code: hasReferralDiscount ? referralCode.trim().toUpperCase() : null,
        original_price: parseFloat(planData.price),
        discount_amount: hasReferralDiscount ? discountAmount : null,
        alternative_email: differentEmail ? differentEmailAddress : null
      };

      let headers = { 'Content-Type': 'application/json', 'User-Agent': 'Airvoy-Web/1.0' };
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }

      const paymentIntentResponse = await fetch(`${BASE_URL}/api/esims/create-payment-intent/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(purchaseData)
      });

      if (!paymentIntentResponse.ok) {
        const errorData = await paymentIntentResponse.json();
        throw new Error(errorData.error || 'Failed to create payment intent');
      }

      const { client_secret } = await paymentIntentResponse.json();

      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElementRef.current,
          billing_details: { name: fullName, email: billingEmail },
        },
      });

      if (error) throw new Error(error.message);

      if (paymentIntent.status === 'succeeded') {
        const completeResponse = await fetch(`${BASE_URL}/api/esims/complete-purchase/`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ ...purchaseData, payment_intent_id: paymentIntent.id })
        });

        if (completeResponse.ok) {
          const result = await completeResponse.json();
          setPaymentSuccess(true);

          const targetEmail = user && differentEmail
            ? t('checkout.success.sentBoth', { a: user.email, b: differentEmailAddress })
            : t('checkout.success.sentOne', { email: billingEmail });

          setSuccessMessage(t('checkout.success.message', { iccid: result.iccid, targetEmail }));
          setShowSuccessNotification(true);

          setTimeout(() => {
            setShowSuccessNotification(false);
            window.location.href = '/dashboard';
          }, 3000);
        } else {
          const errorData = await completeResponse.json();
          throw new Error(errorData.error || 'Failed to complete purchase');
        }
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert(t('checkout.alerts.purchaseFailed', { msg: error.message }));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7B3EF0] mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">{t('checkout.loading')}</p>
        </div>
      </div>
    );
  }

  if (!planData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('checkout.invalidPlan.title')}</h3>
          <p className="text-gray-600 mb-4">{t('checkout.invalidPlan.subtitle')}</p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="px-6 py-3 bg-[#7B3EF0] text-white rounded-xl hover:bg-[#6b33e6] transition-colors font-medium"
          >
            {t('checkout.invalidPlan.cta')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-gray-900">
      {showSuccessNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('checkout.success.title')}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">{successMessage}</p>
              <div className="space-y-3">
                <button
                  onClick={() => { setShowSuccessNotification(false); window.location.href = '/dashboard'; }}
                  className="w-full px-4 py-3 bg-[#7B3EF0] text-white rounded-xl font-medium hover:bg-[#6b33e6] transition-colors"
                >
                  {t('checkout.success.view')}
                </button>
                <button
                  onClick={() => setShowSuccessNotification(false)}
                  className="w-full px-4 py-3 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                >
                  {t('checkout.common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-[#7B3EF0] rounded-lg flex items-center justify-center">
              <Lock className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('checkout.header.title')}</h1>
              <p className="text-gray-600 text-sm">{t('checkout.header.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left */}
          <div className="lg:col-span-2 space-y-6">
            {!user && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-start space-x-3">
                  <User className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900 mb-1">{t('checkout.benefits.title')}</h3>
                    <p className="text-blue-700 text-sm mb-3">{t('checkout.benefits.copy')}</p>
                    <button
                      onClick={() => setShowAuthModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      {t('checkout.benefits.cta')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Billing */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <User className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{t('checkout.billing.title')}</h2>
                      <p className="text-gray-600 text-sm">{t('checkout.billing.subtitle')}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.form.firstName')} *
                      </label>
                      <input
                        type="text"
                        placeholder={t('checkout.placeholder.firstName')}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#7B3EF0] focus:outline-none focus:ring-2 focus:ring-[#7B3EF0] focus:ring-opacity-20"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.form.lastName')} *
                      </label>
                      <input
                        type="text"
                        placeholder={t('checkout.placeholder.lastName')}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#7B3EF0] focus:outline-none focus:ring-2 focus:ring-[#7B3EF0] focus:ring-opacity-20"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('checkout.form.email')} *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        placeholder={t('checkout.placeholder.email')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`w-full pl-11 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#7B3EF0] focus:outline-none focus:ring-2 focus:ring-[#7B3EF0] focus:ring-opacity-20 ${user && !differentEmail ? 'bg-gray-50' : ''}`}
                        required
                        disabled={user && !differentEmail}
                      />
                    </div>
                  </div>

                  {/* Referral */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('checkout.form.referral')}
                    </label>
                    <input
                      type="text"
                      placeholder={t('checkout.placeholder.referral')}
                      value={referralCode}
                      onChange={(e) => {
                        const value = e.target.value;
                        setReferralCode(value);
                        if (value.length === 8) {
                          validateReferralCode(value);
                        } else if (value.length === 0) {
                          validateReferralCode('');
                        } else {
                          setHasReferralDiscount(false);
                          setDiscountPercent(0);
                          setDiscountAmount(0);
                          setReferralCodeStatus('');
                          setIsValidatingReferralCode(false);
                        }
                      }}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#7B3EF0] focus:outline-none focus:ring-2 focus:ring-[#7B3EF0] focus:ring-opacity-20"
                      maxLength={8}
                    />
                    {isValidatingReferralCode && (
                      <div className="flex items-center space-x-2 mt-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#7B3EF0]"></div>
                        <span className="text-sm text-gray-600">{t('checkout.referral.validating')}</span>
                      </div>
                    )}
                    {referralCodeStatus && !isValidatingReferralCode && (
                      <div className={`text-sm mt-2 font-medium ${hasReferralDiscount ? 'text-green-600' : 'text-red-600'}`}>
                        {referralCodeStatus}
                      </div>
                    )}
                  </div>

                  {user && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={differentEmail}
                          onChange={(e) => setDifferentEmail(e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-[#7B3EF0] focus:ring-[#7B3EF0]"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {t('checkout.altEmail.title')}
                          </span>
                          <p className="text-xs text-gray-600 mt-1">
                            {t('checkout.altEmail.copy')}
                          </p>
                        </div>
                      </label>
                    </div>
                  )}

                  {differentEmail && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.altEmail.input')} *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          placeholder={t('checkout.placeholder.altEmail')}
                          value={differentEmailAddress}
                          onChange={(e) => setDifferentEmailAddress(e.target.value)}
                          className="w-full pl-11 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#7B3EF0] focus:outline-none focus:ring-2 focus:ring-[#7B3EF0] focus:ring-opacity-20"
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 pt-4">
                    {!user && (
                      <label className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={createAccount}
                          onChange={(e) => setCreateAccount(e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-[#7B3EF0] focus:ring-[#7B3EF0]"
                        />
                        <div>
                          <span className="text-sm text-gray-700">{t('checkout.createAccount.title')}</span>
                          <p className="text-xs text-gray-500 mt-1">{t('checkout.createAccount.copy')}</p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{t('checkout.payment.title')}</h2>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-sm text-green-600 font-medium">{t('checkout.payment.secured')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">{t('checkout.payment.method')}</label>
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('stripe')}
                        className={`w-full p-4 rounded-xl border-2 transition-colors text-left ${paymentMethod === 'stripe' ? 'border-[#7B3EF0] bg-purple-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <img src={Cardlogo} alt="Cards" className="w-5 h-5" />
                            <div>
                              <span className={`font-medium ${paymentMethod === 'stripe' ? 'text-[#7B3EF0]' : 'text-gray-900'}`}>
                                {t('checkout.payment.card')}
                              </span>
                              <p className="text-xs text-gray-500 mt-1">{t('checkout.payment.cardTypes')}</p>
                            </div>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 ${paymentMethod === 'stripe' ? 'border-[#7B3EF0] bg-[#7B3EF0]' : 'border-gray-300'} flex items-center justify-center`}>
                            {paymentMethod === 'stripe' && <div className="w-2 h-2 bg-white rounded-full" />}
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod('paypal')}
                        className={`w-full p-4 rounded-xl border-2 transition-colors text-left ${paymentMethod === 'paypal' ? 'border-[#7B3EF0] bg-purple-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <img src={PaypalLogo} alt="PayPal" className="w-5 h-5" />
                            <div>
                              <span className={`font-medium ${paymentMethod === 'paypal' ? 'text-[#7B3EF0]' : 'text-gray-900'}`}>
                                PayPal
                              </span>
                              <p className="text-xs text-gray-500 mt-1">{t('checkout.payment.paypalCopy')}</p>
                            </div>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 ${paymentMethod === 'paypal' ? 'border-[#7B3EF0] bg-[#7B3EF0]' : 'border-gray-300'} flex items-center justify-center`}>
                            {paymentMethod === 'paypal' && <div className="w-2 h-2 bg-white rounded-full" />}
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Stripe card */}
                  {paymentMethod === 'stripe' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('checkout.payment.cardInfo')} *
                        </label>
                        <div className="relative">
                          <div className="p-4 bg-white border border-gray-300 rounded-xl min-h-[50px]">
                            <div ref={cardContainerRef} />
                            {stripeLoading && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
                                <div className="flex items-center space-x-2">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#7B3EF0]"></div>
                                  <span className="text-sm text-gray-600">{t('checkout.payment.loadingSecure')}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          {cardError && (
                            <div className="text-red-600 text-sm mt-2 flex items-center space-x-1">
                              <X className="w-4 h-4" />
                              <span>{cardError}</span>
                            </div>
                          )}
                          {cardComplete && !cardError && (
                            <div className="text-green-600 text-sm mt-2 flex items-center space-x-1">
                              <Check className="w-4 h-4" />
                              <span>{t('checkout.payment.cardComplete')}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {(paymentMethod === 'stripe' || paymentMethod === 'paypal') && (
                        <div className="text-xs text-gray-600 mb-4">
                          {t('checkout.legal.agree')}{' '}
                          <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-[#7B3EF0] hover:text-[#6b33e6] underline">
                            {t('checkout.legal.terms')}
                          </a>{' '}{t('checkout.legal.and')}{' '}
                          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#7B3EF0] hover:text-[#6b33e6] underline">
                            {t('checkout.legal.privacy')}
                          </a>
                          .
                        </div>
                      )}

                      {paymentMethod === 'stripe' && (
                        <div className="flex justify-end">
                          <button
                            type="submit"
                            disabled={processing || (!cardComplete || stripeLoading || !isCardMountedRef.current)}
                            className="w-full sm:w-auto sm:px-8 mt-4 bg-gradient-to-r from-[#7B3EF0] to-[#6b33e6] hover:from-[#6b33e6] hover:to-[#5a2bd1] disabled:from-gray-300 disabled:to-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed text-white py-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:shadow-none"
                          >
                            {processing ? (
                              <div className="flex items-center justify-center space-x-2">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                <span>{t('checkout.payment.processing')}</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center space-x-2">
                                <Lock className="w-5 h-5" />
                                <span>{t('checkout.payment.complete', { total: totalWithFees().toFixed(2) })}</span>
                              </div>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* PayPal */}
                  {paymentMethod === 'paypal' && (
                    <div className="space-y-4">
                      <div>
                        <div id="paypal-button-container" className="min-h-[50px]"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Right - Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">{t('checkout.summary.title')}</h2>
                  </div>
                </div>

                <div className={`${showOrderSummary ? 'block' : 'hidden lg:block'}`}>
                  {/* Plan details */}
                  <div className="p-6 border-b border-gray-200">
                    <div className="bg-white rounded-xl p-4 text-black border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <Smartphone className="w-5 h-5" />
                          <span className="font-medium">{t('checkout.summary.planLabel')}</span>
                        </div>
                        <span className="text-sm bg-gray-100 px-2 py-1 rounded-full">
                          {t('checkout.summary.digital')}
                        </span>
                      </div>
 
                      <h3 className="text-xl font-bold mb-2">{planData.country}</h3>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">{t('checkout.summary.data')}</span>
                          <span className="font-semibold">{planData.dataAmount}</span>
                        </div> 
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">{t('checkout.summary.validity')}</span>
                          <span className="font-semibold">
                            {t('checkout.duration.days', { count: planData.duration })}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">{t('checkout.summary.coverage')}</span>
                          <span className="font-semibold">{t('checkout.summary.nationwide')}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="p-6 border-b border-gray-200">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">{t('checkout.pricing.planPrice')}</span>
                        <span className="font-medium text-gray-900">${planData.price}</span>
                      </div>

                      {hasReferralDiscount && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-green-600">
                            {t('checkout.pricing.referralDiscount', { percent: discountPercent })}
                          </span>
                          <span className="font-medium text-green-600">-${discountAmount.toFixed(2)}</span>
                        </div>
                      )}

                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center space-x-1">
                          <span className="text-gray-600">
                            {paymentMethod === 'stripe' ? t('checkout.pricing.stripeFee') : t('checkout.pricing.paypalFee')}
                          </span>
                          <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="font-medium text-gray-900">
                          +${calculatePaymentFee(getFinalPrice()).toFixed(2)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">{t('checkout.pricing.taxes')}</span>
                        <span className="font-medium text-green-600">{t('checkout.pricing.included')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="p-6">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-gray-900">{t('checkout.pricing.total')}</span>
                      <div className="text-right">
                        <div className="text-xl text-gray-900">${totalWithFees().toFixed(2)}</div>
                        <div className="text-sm text-gray-600">USD</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>    
    </div>
  );
};

export default Checkout;